/**
 * Instant Bank Discount (IBD) specific form validators.
 *
 * Validates IBD campaign-specific fields: bank names, instruments, event type,
 * terms & conditions, campaign budget, number of times incentivized, and guardrails.
 * Extracted from formValidationUtils for single-responsibility.
 *
 * @module instantBankDiscountValidation
 */

import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { parseOptionalNumber } from 'src/utils/helpers';
import { FieldErrors } from '../formValidationUtils';

// ============================================================================
// Public API
// ============================================================================

export function validateInstantBankDiscount(form: UseCampaignFormResult, fieldErrors: FieldErrors): void {
    // ── Attribute validations ───────────────────────────────────────────

    // Bank names — at least one required
    if (!form.ibdBankNames || form.ibdBankNames.length === 0) {
        fieldErrors.ibdBankNames = { key: 'ibd_bank_names_empty' };
    }

    // Instruments — at least one required
    if (!form.ibdInstruments || form.ibdInstruments.length === 0) {
        fieldErrors.ibdInstruments = { key: 'ibd_instruments_empty' };
    }

    // Event type — required
    if (!form.ibdEventType) {
        fieldErrors.ibdEventType = { key: 'ibd_event_type_empty' };
    }

    // Terms and conditions — non-empty
    if (!form.ibdTermsAndConditions || form.ibdTermsAndConditions.trim().length === 0) {
        fieldErrors.ibdTermsAndConditions = { key: 'ibd_terms_empty' };
    }

    // Campaign budget — required, positive integer
    const budget = parseOptionalNumber(form.ibdCampaignBudget);
    if (budget == null || budget <= 0 || !Number.isInteger(budget)) {
        fieldErrors.ibdCampaignBudget = { key: 'ibd_budget_invalid' };
    }

    // maxTimesRewardAvailable — required, integer >= 1, must divide budget evenly.
    // Business rule: the budget is split equally across reward slots, so budget / maxTimesRewardAvailable
    // must yield a whole number (the per-reward amount). A non-divisible value would result in
    // uneven reward distribution which the backend rejects.
    const numTimes = parseOptionalNumber(form.ibdMaxTimesRewardAvailable);
    if (numTimes == null || numTimes < 1 || !Number.isInteger(numTimes)) {
        fieldErrors.ibdMaxTimesRewardAvailable = { key: 'ibd_max_times_reward_invalid' };
    } else if (budget != null && budget > 0 && Number.isInteger(budget) && budget % numTimes !== 0) {
        fieldErrors.ibdMaxTimesRewardAvailable = { key: 'ibd_max_times_reward_not_divisor' };
    }

    // Max cap per transaction — required, > 0
    const maxCapTxn = parseOptionalNumber(form.ibdMaxRewardValuePerTransaction);
    if (maxCapTxn == null || maxCapTxn <= 0) {
        fieldErrors.ibdMaxRewardValuePerTransaction = { key: 'ibd_max_reward_value_per_transaction_invalid' };
    }

    // Max cap per promotion — required, > 0
    const maxCapPromo = parseOptionalNumber(form.ibdMaxRewardValuePerPromotion);
    if (maxCapPromo == null || maxCapPromo <= 0) {
        fieldErrors.ibdMaxRewardValuePerPromotion = { key: 'ibd_max_reward_value_per_promotion_invalid' };
    }

    // ── Guardrail validations ───────────────────────────────────────────

    // Percent extent of competitor guardrail — required, 1–100
    const extent = parseOptionalNumber(form.ibdPercentExtentOfCompetitorGuardrail);
    if (extent == null || extent < 1 || extent > 100) {
        fieldErrors.ibdPercentExtentOfCompetitorGuardrail = { key: 'ibd_extent_guardrail_range' };
    }

    // Percent of ASIN price guardrail — optional, but if provided must be 0–100
    const percentAsin = parseOptionalNumber(form.ibdPercentOfASINPriceGuardrail);
    if (percentAsin != null && (percentAsin < 0 || percentAsin > 100)) {
        fieldErrors.ibdPercentOfASINPriceGuardrail = { key: 'ibd_percent_asin_range' };
    }

    // Absolute value guardrail — optional, but if provided must be >= 1
    const absoluteValue = parseOptionalNumber(form.ibdAbsoluteValueGuardrail);
    if (absoluteValue != null && absoluteValue < 1) {
        fieldErrors.ibdAbsoluteValueGuardrail = { key: 'ibd_absolute_value_range' };
    }
}
